/**
 * The portfolio view: every open task, grouped the way the project screen
 * groups them — project, then deliverable, then work.
 *
 * PM only. The server enforces that (db/overview.ts); this screen just stops
 * offering it, and explains itself if someone arrives by a pasted link.
 *
 * Filters are applied BEFORE grouping, deliberately. A deliverable heading that
 * survives after all its tasks are filtered out is a lie about what is left.
 */
import type { Overview as OverviewData, OverviewTask, ReferenceOption } from "@fieldpath/shared";
import { mdy } from "@fieldpath/shared";
import { useEffect, useMemo, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { HelpButton } from "./HelpButton";
import { KindTag } from "./KindTag";
import { SearchSelect } from "./SearchSelect";
import { useTaskHover } from "./TaskHoverCard";
import type { TaskHoverBind } from "./TaskHoverCard";

interface Props {
  fetcher: Fetcher;
  onOpenTask: (taskId: string) => void;
  onOpenProject: (projectId: string) => void;
}

const STATUSES = ["Not Started", "In Progress", "Blocked", "Done"] as const;
/**
 * "Due within" choices, all WORKING days since punch list #13 (12 Oct 2026,
 * and Jeremy's yes to 30 the same day); the server says how many calendar
 * days each reaches, holidays included.
 */
const HORIZONS = [
  { key: "w5", label: "5 working days", working: 5 },
  { key: "w10", label: "10 working days", working: 10 },
  { key: "w30", label: "30 working days", working: 30 },
] as const;
type HorizonKey = (typeof HORIZONS)[number]["key"];

export function Overview({ fetcher, onOpenTask, onOpenProject }: Props) {
  // One hook per screen: `bind` goes on each row, `card` renders the portal.
  const { bind, card } = useTaskHover(fetcher);
  // Asked once, so a row can mark which of these people is you. An existing
  // endpoint; a failure means no highlight rather than no screen.
  const [meKey, setMeKey] = useState<string | null>(null);

  useEffect(() => {
    void api
      .me(fetcher)
      .then((r) => setMeKey(r.employee?.ajeraKey ?? null))
      .catch(() => setMeKey(null));
  }, [fetcher]);
  const [data, setData] = useState<OverviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const [q, setQ] = useState("");
  // Done is off by default: a book of work is about what is still in front of
  // people. It is a toggle rather than a filter that is absent, so the choice
  // is visible instead of a silent omission.
  const [statuses, setStatuses] = useState<Set<string>>(
    () => new Set(["Not Started", "In Progress", "Blocked"]),
  );
  const [pastDue, setPastDue] = useState(false);
  const [horizonKey, setHorizonKey] = useState<HorizonKey | null>(null);
  // Calendar days that 5 and 10 working days reach, from the server. Until
  // they arrive (or if they cannot be had) those two buttons are disabled
  // rather than filtering by a guess.
  const [workingReach, setWorkingReach] = useState<Readonly<Record<number, number>> | null>(null);

  useEffect(() => {
    let live = true;
    void Promise.all(HORIZONS.map((h) => api.businessWindow(fetcher, h.working)))
      .then((windows) => {
        if (live) {
          setWorkingReach(
            Object.fromEntries(HORIZONS.map((h, i) => [h.working, windows[i]!.calendarDays])),
          );
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [fetcher]);

  /** The chosen horizon in calendar days from today, or null for any date. */
  const horizon = useMemo(() => {
    const h = HORIZONS.find((x) => x.key === horizonKey);
    if (!h) return null;
    return workingReach?.[h.working] ?? null;
  }, [horizonKey, workingReach]);
  const [person, setPerson] = useState("");
  const [pm, setPm] = useState("");

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    let current = true;
    api
      .overview(fetcher)
      .then((d) => { if (current) { setData(d); setError(null); } })
      .catch((e: unknown) => {
        if (!current) return;
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        else setError(e instanceof Error ? e.message : String(e));
      });
    return () => { current = false; };
  }, [fetcher, back]);

  /*
   * Keyed by the Ajera key, not the name.
   *
   * **This fixed a latent bug while it was being changed.** The list was a
   * `Set<string>` of NAMES, so two people with the same display name collapsed
   * into one option, and picking it filtered to both of them with nothing on
   * screen saying so. The key is the identity; the name is the label.
   */
  const people = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const t of data?.tasks ?? []) for (const w of t.withWhom) byKey.set(w.key, w.name);
    return [...byKey.entries()]
      .map(([key, name]) => ({ key: key as ReferenceOption["key"], label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  // Matched by NAME (`ownerName`), so the name is the key here as well.
  const managers = useMemo(() => {
    const names = new Set<string>();
    for (const t of data?.tasks ?? []) if (t.ownerName) names.add(t.ownerName);
    return [...names].sort().map((name) => ({ key: name as ReferenceOption["key"], label: name }));
  }, [data]);

  const tasks = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.tasks ?? []).filter((t) => {
      if (!statuses.has(t.status)) return false;
      if (pastDue && !(t.daysUntilDue !== null && t.daysUntilDue < 0)) return false;
      // Past due and a horizon read as "late OR soon", not "late AND soon" —
      // asking for both at once otherwise returns nothing, which looks broken.
      if (horizon !== null && !pastDue) {
        if (t.daysUntilDue === null || t.daysUntilDue > horizon) return false;
      }
      if (person && !t.withWhom.some((w) => w.key === person)) return false;
      if (pm && t.ownerName !== pm) return false;
      if (needle) {
        const hay = [t.taskTitle, t.projectName, t.clientName, t.deliverableName, t.currentStage]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [data, q, statuses, pastDue, horizon, person, pm]);

  /** Project → deliverable → tasks, with ungrouped work directly under the project. */
  const projects = useMemo(() => {
    const out = new Map<
      string,
      {
        id: string;
        name: string;
        clientName: string | null;
        loose: OverviewTask[];
        groups: Map<string, { id: string; name: string; due: string | null; tasks: OverviewTask[] }>;
      }
    >();
    for (const t of tasks) {
      let p = out.get(t.projectId);
      if (!p) {
        p = { id: t.projectId, name: t.projectName, clientName: t.clientName, loose: [], groups: new Map() };
        out.set(t.projectId, p);
      }
      if (t.deliverableId === null) {
        p.loose.push(t);
      } else {
        let g = p.groups.get(t.deliverableId);
        if (!g) {
          g = {
            id: t.deliverableId,
            name: t.deliverableName ?? "Deliverable",
            due: t.deliverableDueDate,
            tasks: [],
          };
          p.groups.set(t.deliverableId, g);
        }
        g.tasks.push(t);
      }
    }
    return [...out.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [tasks]);

  if (forbidden) {
    return (
      <div className="card">
        <h2>This view is for project managers and admins</h2>
        <p className="muted">
          The overview shows every project's work in one place, so it is limited to people
          Deltek flags as a project manager or an admin. Your record is flagged as neither —
          if that is wrong, it is corrected in Deltek rather than here.
        </p>
      </div>
    );
  }
  if (error) return <div className="error">{error}</div>;
  if (!data) return <p className="muted">Loading…</p>;

  const toggleStatus = (s: string) =>
    setStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });

  return (
    <>
      {card}
      {/* The count goes ABOVE the heading, mono, as on the other screens:
          it is what the page is showing, not a label on the title. */}
      <div className="count-line">
        {tasks.length} OF {data.tasks.length} TASKS
      </div>
      <h1 className="page-h1">
        Overview
        <HelpButton screens="overview" />
      </h1>

      <div className="filters">
        <input
          type="search"
          value={q}
          placeholder="Search task, project, client or deliverable…"
          aria-label="Search"
          onChange={(e) => setQ(e.target.value)}
          className="filter-search"
        />

        <div className="filter-row">
          {STATUSES.map((s) => (
            <button
              key={s}
              className={`chip${statuses.has(s) ? " on" : ""}`}
              aria-pressed={statuses.has(s)}
              onClick={() => toggleStatus(s)}
            >
              {s}
            </button>
          ))}

          <button
            className={`chip${pastDue ? " on alarm" : ""}`}
            aria-pressed={pastDue}
            onClick={() => setPastDue((v) => !v)}
          >
            Past due
          </button>

          <div className="viewpick" role="group" aria-label="Due within">
            <button
              className={`seg${horizonKey === null ? " on" : ""}`}
              aria-pressed={horizonKey === null}
              onClick={() => setHorizonKey(null)}
            >
              Any date
            </button>
            {HORIZONS.map((h) => (
              <button
                key={h.key}
                className={`seg${horizonKey === h.key ? " on" : ""}`}
                aria-pressed={horizonKey === h.key}
                disabled={workingReach === null}
                onClick={() => setHorizonKey(h.key)}
              >
                {h.label}
              </button>
            ))}
          </div>

          {/* Type to find, like every list of people (punch list #27). */}
          <div className="filter-pick-ss">
            <SearchSelect
              ariaLabel="With whom"
              inputClassName="filter-pick"
              options={people}
              value={person}
              emptyLabel="Anyone holds it"
              onChange={setPerson}
            />
          </div>
          <div className="filter-pick-ss">
            <SearchSelect
              ariaLabel="PM / Task Manager"
              inputClassName="filter-pick"
              options={managers}
              value={pm}
              emptyLabel="Any PM / Task Manager"
              onChange={setPm}
            />
          </div>
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="empty">
          {data.tasks.length === 0
            ? "No open work anywhere yet."
            : "Nothing matches those filters."}
        </div>
      ) : (
        /*
         * NO PROJECT SECTIONS (punch list #12, 12 Oct 2026). A project holds
         * no work of its own -- only what is under it does -- so a project
         * heading was a section with nothing in it but other sections. Each
         * deliverable is now a boxed card naming its project on its own
         * line, with its tasks indented inside; a task in no deliverable is
         * a boxed line of its own naming its project. Still ordered by
         * project, then deliverable, then loose work, so one project's work
         * stays together.
         */
        <div className="ov-units">
          {projects.flatMap((p) => [
            ...[...p.groups.values()]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((g) => (
                // Keyed by the deliverable's ID. It was keyed by NAME, so two
                // deliverables with one name collided. Found while moving it.
                <section key={g.id} className="ov-unit">
                  <div className="ov-unit-head">
                    <KindTag kind="deliverable" size="row" />
                    <span className="deliv-name">{g.name}</span>
                    {g.due ? <span className="muted"> · due {mdy(g.due)}</span> : null}
                    <ProjectNote project={p} onOpen={onOpenProject} />
                  </div>
                  {g.tasks.map((t) => (
                    <Row key={t.taskId} task={t} onOpen={onOpenTask} hover={bind} meKey={meKey} />
                  ))}
                </section>
              )),
            ...p.loose.map((t) => (
              <section key={t.taskId} className="ov-unit loose">
                <Row task={t} onOpen={onOpenTask} hover={bind} meKey={meKey} />
                <div className="ov-loose-project">
                  <ProjectNote project={p} onOpen={onOpenProject} />
                </div>
              </section>
            )),
          ])}
        </div>
      )}
    </>
  );
}

/** "PRJ Riverside ESA · Deriva", on the deliverable's or the loose task's line. */
function ProjectNote({
  project,
  onOpen,
}: {
  project: { id: string; name: string; clientName: string | null };
  onOpen: (projectId: string) => void;
}) {
  return (
    <span className="ov-project-note">
      <button className="linkish ov-project-link" onClick={() => onOpen(project.id)}>
        <KindTag kind="project" size="crumb" />
        {project.name}
      </button>
      {project.clientName ? (
        <span className="muted">
          {" · "}
          <span className="client-name">{project.clientName}</span>
        </span>
      ) : null}
    </span>
  );
}

function Row({
  task,
  onOpen,
  hover,
  meKey,
}: {
  task: OverviewTask;
  onOpen: (id: string) => void;
  hover: TaskHoverBind;
  /** The signed-in person's Ajera KEY. Never matched on a display name. */
  meKey: string | null;
}) {
  const late = task.daysUntilDue !== null && task.daysUntilDue < 0 && task.status !== "Done";
  return (
    <button
      className={`ov-task${late ? " overdue" : ""}`}
      onClick={() => onOpen(task.taskId)}
      {...hover(task.taskId, task.taskTitle)}
    >
      <span className="ov-title task-name">
        <KindTag kind="task" size="row" />
        {task.taskTitle}
      </span>
      <span className="ov-stage muted">
        {/* A badge rather than a "Blocked — " prefix, matching My work. The
            prefix put the most important word on the row in the same weight
            and colour as the stage name after it. */}
        {task.isBlocked ? <span className="blocked-badge">BLOCKED</span> : null}
        {task.currentStage ?? task.status}
      </span>
      <span className="ov-who muted">
        {task.withWhom.length === 0
          ? "nobody"
          : /* Each name its own span, so yours can be marked without marking
               the people beside it. */
            task.withWhom.map((w, i) => (
              <span key={w.key}>
                {i > 0 ? ", " : ""}
                <span className={w.key === meKey ? "mine-name" : undefined}>
                  {w.name}
                </span>
              </span>
            ))}
      </span>
      <span className={`ov-due${late ? " late" : ""}`}>{due(task)}</span>
    </button>
  );
}

function due(task: OverviewTask): string {
  if (task.dueDate === null) return "no date";
  const d = task.daysUntilDue;
  if (d === null || task.status === "Done") return mdy(task.dueDate);
  if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} late`;
  if (d === 0) return "today";
  if (d === 1) return "tomorrow";
  if (d <= 5) return `in ${d} days`;
  return mdy(task.dueDate);
}
